import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import IconButton from "./IconButton";
import "./Modal.css";

function Modal({
    open,
    onClose,
    title,
    eyebrow,
    width = 480,
    children,
    footer,
    closeOnBackdrop = true,
    initialFocus,
}) {
    const dialogRef = useRef(null);
    const previouslyFocused = useRef(null);

    useEffect(() => {
        if (!open) return;
        previouslyFocused.current = document.activeElement;

        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose?.();
            } else if (e.key === "Tab") {
                const focusable = dialogRef.current?.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (!focusable?.length) return;
                const list = Array.from(focusable).filter(
                    (el) => !el.disabled && el.offsetParent !== null
                );
                if (!list.length) return;
                const first = list[0];
                const last = list[list.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        const target = initialFocus?.current ?? dialogRef.current;
        target?.focus?.();
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
            previouslyFocused.current?.focus?.();
        };
    }, [open, onClose, initialFocus]);

    if (!open) return null;

    return (
        <div
            className="modal-backdrop"
            onMouseDown={(e) => {
                if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
            }}
        >
            <div
                ref={dialogRef}
                className="modal-dialog"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                style={{ "--modal-width": `${width}px` }}
            >
                <header className="modal-dialog__head">
                    <div className="modal-dialog__head-text">
                        {eyebrow && (
                            <div className="modal-dialog__eyebrow uppercase-label">
                                {eyebrow}
                            </div>
                        )}
                        <h2 className="modal-dialog__title">{title}</h2>
                    </div>
                    <IconButton
                        icon={X}
                        label="Close"
                        size="sm"
                        onClick={onClose}
                    />
                </header>
                <div className="modal-dialog__body">{children}</div>
                {footer && <footer className="modal-dialog__foot">{footer}</footer>}
            </div>
        </div>
    );
}

export default Modal;
